import { type ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { CloseIcon, DeleteIcon, EditIcon } from '@/components/Icons'
import { MoreHorizontal, ArrowUpDown, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { UserActionDialog } from '@/components/UserActionDialog'
import { showSuccessToast } from '../ui/toast'
import { PATHROUTES } from '@/constants/path'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AdvertisementActions,
  getAdvertisementStatusBadgeProps,
  transformAdvertisementPlacement,
  type AdvertisementActionsType,
} from '@/constants/sponsor'
import { useMutation } from '@tanstack/react-query'
import { updateAdvertisementStatus } from '@/api/sponsorApi'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { queryClient } from '@/main'
import { getPlainText, handleAxiosError } from '@/utils/helper'
import { AdvertisementStatus } from '@/schemas/sponsor.schems'

export type AdvertisementStatus =
  | 'active'
  | 'inactive'
  | 'expired'
  | 'draft'
  | 'rejected'

export interface Advertisement {
  id: string
  sponsorShipId: string
  adName: string
  description: string
  placement: string
  duration: string
  status: AdvertisementStatus
  performance: {
    impressions: number
    clicks: number
    ctr: number
  }
  sponsorName?: string
  eventName?: string
  isChangesRequired: boolean
  sponsorId: string
  subEventName?: string
}

const AdvertisementActionsMenu = ({
  advertisement,
}: {
  advertisement: Advertisement
}) => {
  const [userActionDialogOpen, setUserActionDialogOpen] = useState(false)
  const [advertisementAction, setAdvertisementAction] =
    useState<AdvertisementActionsType | null>(null)
  const isActive = advertisement.status === AdvertisementStatus.ACTIVE
  const isPending = advertisement.status === AdvertisementStatus.INACTIVE
  const isExpired = advertisement.status === AdvertisementStatus.EXPIRED
  const isRejected = advertisement.status === AdvertisementStatus.REJECTED
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const {
    mutate: updateAdvertisementStatusMutation,
    isPending: isUpdatingAdvertisementStatus,
  } = useMutation({
    mutationFn: updateAdvertisementStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(advertisement.id)],
      })

      setUserActionDialogOpen(false)
      let toastTitle = ''
      let toastDesc = ''
      if (advertisementAction === AdvertisementActions.active) {
        toastTitle = 'Ad Restarted'
        toastDesc =
          'Your ad is now live again on the event website. You can monitor its performance in your dashboard.'
      } else if (advertisementAction === AdvertisementActions.inactive) {
        toastTitle = 'Ad Paused'
        toastDesc =
          'This ad has been successfully paused and is no longer visible on the event website. You can resume it anytime from your dashboard.'
      } else if (advertisementAction === AdvertisementActions.delete) {
        toastTitle = 'Ad Deleted'
        toastDesc = 'This advertisement has been successfully deleted.'
      }
      showSuccessToast(toastTitle, toastDesc)
      setAdvertisementAction(null)
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement status', error)
    },
  })

  const handleSetAction = (action: AdvertisementActionsType) => {
    setAdvertisementAction(action)
    setUserActionDialogOpen(true)
  }

  const handleActionClick = async (action: AdvertisementActionsType) => {
    setAdvertisementAction(action)
    updateAdvertisementStatusMutation({
      id: advertisement.id,
      payload: {
        status: action,
      },
    })
  }

  let dialogTitle = ''
  let dialogSubtext = ''
  let dialogButton = ''
  let dialogDanger = false
  if (advertisementAction === AdvertisementActions.delete) {
    dialogTitle = 'Delete Advertisement?'
    dialogSubtext =
      'Are you sure you want to delete this advertisement? This action cannot be undone.'
    dialogButton = 'Delete'
    dialogDanger = true
  } else if (advertisementAction === AdvertisementActions.active) {
    dialogTitle = 'Are you sure you want to resume this Ad?'
    dialogSubtext =
      'Resuming will immediately make this ad live again on the event website. Performance tracking will continue from where it left off.'
    dialogButton = 'Resume Ad'
  } else if (advertisementAction === AdvertisementActions.inactive) {
    dialogTitle = 'Are you sure you want to pause this Ad?'
    dialogSubtext =
      'Pausing will immediately stop this ad from displaying on the event website. You can resume it anytime from your dashboard.'
    dialogButton = 'Pause Ad'
    dialogDanger = true
  }

  return (
    <>
      {!isExpired && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-w-fit">
            {!isRejected && (
              <DropdownMenuItem
                onClick={() => {
                  navigate(
                    `${PATHROUTES.DASHBOARD.UPLOAD_AD_WITH_ID(advertisement.sponsorShipId)}?adId=${advertisement.id}&&sponsorshipId=${id!}`
                  )
                }}
              >
                <EditIcon className="h-4 w-4" />
                Edit Ad
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => handleSetAction(AdvertisementActions.delete)}
            >
              <DeleteIcon className="h-4 w-4" strokeWidth={1.5} />
              Delete
            </DropdownMenuItem>
            {isActive && (
              <DropdownMenuItem
                className="text-ss-critical-200 focus:text-ss-critical-200"
                onClick={() => handleSetAction(AdvertisementActions.inactive)}
              >
                <CloseIcon className="h-4 w-4" />
                Pause Ad
              </DropdownMenuItem>
            )}
            {isPending && (
              <DropdownMenuItem
                className="text-ss-critical-200 focus:text-ss-critical-200"
                onClick={() => handleSetAction(AdvertisementActions.active)}
              >
                <Play className="h-4 w-4" />
                Resume Ad
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <UserActionDialog
        open={userActionDialogOpen}
        onOpenChange={setUserActionDialogOpen}
        showForm={false}
        danger={dialogDanger}
        title={dialogTitle}
        subtext={dialogSubtext}
        confirmButtonText={dialogButton}
        isLoading={isUpdatingAdvertisementStatus}
        onConfirm={() => {
          if (advertisementAction) {
            handleActionClick(advertisementAction)
          }
        }}
      />
    </>
  )
}

export const advertisementColumns: ColumnDef<Advertisement>[] = [
  {
    accessorKey: 'adName',
    header: 'Ad Title',
    cell: ({ row }) => {
      const adName = row.getValue('adName') as string
      return (
        <div className="font-medium max-w-[220px] wrap-anywhere  text-foreground">
          {getPlainText(adName)}
        </div>
      )
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const description = row.getValue('description') as string
      return (
        <div className="max-w-[200px] text-ss-neutral-600 text-sm  wrap-anywhere">
          {getPlainText(description)}
        </div>
      )
    },
  },
  {
    accessorKey: 'placement',
    header: 'Placement',
    cell: ({ row }) => {
      const placement = transformAdvertisementPlacement(
        row.getValue('placement') as string
      )
      return <div className="text-foreground wrap-anywhere">{placement}</div>
    },
  },
  {
    accessorKey: 'duration',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 hover:bg-transparent wrap-anywhere"
        >
          Duration
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const duration = row.getValue('duration') as string
      return <div className="text-foreground">{duration}</div>
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as AdvertisementStatus
      const isChangesRequired = row.original.isChangesRequired
      const badgeProps = getAdvertisementStatusBadgeProps(
        status,
        isChangesRequired
      )
      return (
        <Badge
          variant="outline"
          className={cn(
            'px-2.5 py-1 text-xs font-medium rounded-full',
            badgeProps.className
          )}
        >
          {badgeProps.children}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'performance',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 hover:bg-transparent wrap-anywhere"
        >
          Performance
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const performance = row.getValue(
        'performance'
      ) as Advertisement['performance']
      return (
        <div className="text-foreground">
          <div className="font-medium">
            {performance.impressions.toLocaleString()} impressions
          </div>
          <div className="text-sm text-muted-foreground">
            {performance.clicks.toLocaleString()} clicks (
            {Number(performance.ctr)?.toFixed(1)}% CTR)
          </div>
        </div>
      )
    },
    sortingFn: (rowA, rowB) => {
      const perfA = rowA.getValue('performance') as Advertisement['performance']
      const perfB = rowB.getValue('performance') as Advertisement['performance']
      return perfA.impressions - perfB.impressions
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const advertisement = row.original
      return <AdvertisementActionsMenu advertisement={advertisement} />
    },
  },
]
